import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import subscriptionService from '../../services/subscriptionService';

export const fetchPlans = createAsyncThunk('subscription/fetchPlans', async (_, { rejectWithValue }) => {
  try {
    const response = await subscriptionService.getPlans();
    return response.data;
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || 'Failed to fetch plans');
  }
});

export const createPlan = createAsyncThunk('subscription/createPlan', async (data, { rejectWithValue }) => {
  try {
    const response = await subscriptionService.createPlan(data);
    return response.data;
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || 'Failed to create plan');
  }
});

export const updatePlan = createAsyncThunk('subscription/updatePlan', async ({ id, data }, { rejectWithValue }) => {
  try {
    const response = await subscriptionService.updatePlan(id, data);
    return response.data;
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || 'Failed to update plan');
  }
});

export const deletePlan = createAsyncThunk('subscription/deletePlan', async (id, { rejectWithValue }) => {
  try {
    await subscriptionService.deletePlan(id);
    return id;
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || 'Failed to delete plan');
  }
});

export const fetchSubscriptions = createAsyncThunk('subscription/fetchSubscriptions', async (params, { rejectWithValue }) => {
  try {
    const response = await subscriptionService.getAllSubscriptions(params);
    return response.data;
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || 'Failed to fetch subscriptions');
  }
});

export const fetchStats = createAsyncThunk('subscription/fetchStats', async (_, { rejectWithValue }) => {
  try {
    const response = await subscriptionService.getStats();
    return response.data;
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || 'Failed to fetch stats');
  }
});

export const fetchPayments = createAsyncThunk('subscription/fetchPayments', async (params, { rejectWithValue }) => {
  try {
    const response = await subscriptionService.getAllPayments(params);
    return response.data;
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || 'Failed to fetch payments');
  }
});

export const fetchPaymentStats = createAsyncThunk('subscription/fetchPaymentStats', async (_, { rejectWithValue }) => {
  try {
    const response = await subscriptionService.getPaymentStats();
    return response.data;
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || 'Failed to fetch payment stats');
  }
});

export const grantSubscription = createAsyncThunk('subscription/grant', async (data, { rejectWithValue }) => {
  try {
    const response = await subscriptionService.grantSubscription(data);
    return response.data;
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || 'Failed to activate subscription');
  }
});

const subscriptionSlice = createSlice({
  name: 'subscription',
  initialState: {
    plans: [],
    subscriptions: [],
    payments: [],
    stats: null,
    paymentStats: null,
    totalSubscriptions: 0,
    totalPayments: 0,
    totalPages: 0,
    totalPaymentPages: 0,
    isLoading: false,
    success: false,
    error: null,
  },
  reducers: {
    resetSuccess: (state) => {
      state.success = false;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Plans
      .addCase(fetchPlans.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchPlans.fulfilled, (state, action) => {
        state.isLoading = false;
        state.plans = action.payload.data;
      })
      .addCase(fetchPlans.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Create Plan
      .addCase(createPlan.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(createPlan.fulfilled, (state, action) => {
        state.isLoading = false;
        state.plans.push(action.payload.data);
        state.success = true;
      })
      .addCase(createPlan.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Update Plan
      .addCase(updatePlan.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(updatePlan.fulfilled, (state, action) => {
        state.isLoading = false;
        const index = state.plans.findIndex((p) => p._id === action.payload.data._id);
        if (index !== -1) {
          state.plans[index] = action.payload.data;
        }
        state.success = true;
      })
      .addCase(updatePlan.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Delete Plan
      .addCase(deletePlan.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(deletePlan.fulfilled, (state, action) => {
        state.isLoading = false;
        state.plans = state.plans.filter((p) => p._id !== action.payload);
        state.success = true;
      })
      .addCase(deletePlan.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Fetch Subscriptions
      .addCase(fetchSubscriptions.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchSubscriptions.fulfilled, (state, action) => {
        state.isLoading = false;
        state.subscriptions = action.payload.data;
        state.totalSubscriptions = action.payload.pagination?.totalItems || 0;
        state.totalPages = action.payload.pagination?.totalPages || 0;
      })
      .addCase(fetchSubscriptions.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Fetch Stats
      .addCase(fetchStats.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchStats.fulfilled, (state, action) => {
        state.isLoading = false;
        state.stats = action.payload.data;
      })
      .addCase(fetchStats.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Fetch Payments
      .addCase(fetchPayments.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchPayments.fulfilled, (state, action) => {
        state.isLoading = false;
        state.payments = action.payload.data;
        state.totalPayments = action.payload.pagination?.totalItems || 0;
        state.totalPaymentPages = action.payload.pagination?.totalPages || 0;
      })
      .addCase(fetchPayments.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Fetch Payment Stats
      .addCase(fetchPaymentStats.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchPaymentStats.fulfilled, (state, action) => {
        state.isLoading = false;
        state.paymentStats = action.payload.data;
      })
      .addCase(fetchPaymentStats.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Grant Subscription
      .addCase(grantSubscription.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(grantSubscription.fulfilled, (state, action) => {
        state.isLoading = false;
        state.subscriptions.unshift(action.payload.data);
        state.success = true;
      })
      .addCase(grantSubscription.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

export const { resetSuccess, clearError } = subscriptionSlice.actions;
export default subscriptionSlice.reducer;
